/**
 * @author Iskra Popova
 *
 */

import { IViewConfigSchema, ILayersList } from "../baseConfigs/layoutConfigShema";
import SceneView from "../views/SceneView";
import BaseTransition from "../../src/transitions/BaseTransition";
import PercentScalableSceneView from "../views/PercentScalableSceneView";
import GameModel from "../../src/model/GameModel";
import App from "../App";
import Layout from "./Layout";
import Utils from "./Utils";
import ISceneOptions from "../contracts/ISceneOptions";

export default class Layer extends PIXI.Container {

    protected activeSceneName: string;
    protected defaultSceneName: string;
    protected activeScene: SceneView;
    protected transitionScene: SceneView;
    protected scenes: string[];
    protected layersConfig: ILayersList;
    protected secundarylayersConfig: ILayersList;
    protected staticBound:PIXI.Rectangle;

    constructor(scenes: string[],staticBound:PIXI.Rectangle, layersConfig: ILayersList, defaultSceneName: string, secundarylayersConfig: ILayersList) {
        super();
        this.scenes = scenes;
        this.staticBound = staticBound;
        this.layersConfig = layersConfig;
        this.secundarylayersConfig = secundarylayersConfig;
        this.name = this.defaultSceneName = defaultSceneName;
    }

    public playDefault() {
        if (this.hasScene(this.defaultSceneName)) {
            this.setScene();
        }
    }

    public findSceneNameContainsBound(boundName: string): string {
        let sceneName = "";
        let reGex = /[\w]+~[\d]+/g
        Object.entries(this.layersConfig).forEach(([key, vl]) => {
            if (vl.groups && Object.entries(vl.groups).length > 0) {
                Object.entries(vl.groups).forEach(([gKey, gValue]) => {
                    gValue.bounds.forEach(bound => {
                        if (bound.id === boundName) {
                            let match = key.match(reGex);
                            if (match && match.length > 0) {
                                sceneName = match[0] || "";
                            }
                        }
                    })
                })
            }
        })

        return sceneName
    }

    public getDefaultSceneName(): string {
        return this.defaultSceneName;
    }

    public getCurrentScene(): SceneView {
        return this.activeScene;
    }

    public getCurrentTransitionScene(): SceneView {
        return this.transitionScene;
    }

    public transitionToScene(sceneName: string, transition: BaseTransition, onIn?: (...args: any[]) => void, onComplete?: (...args: any[]) => void  ) {
        if (!sceneName) {
            sceneName = this.defaultSceneName;
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }

        this.transitionScene = this.createScene(sceneName);
        this.transitionScene.alpha = 0;
        this.addChild(this.transitionScene);
        // const tl = transition.execute();
        transition.in().then(() => {
            if (onIn) {
                onIn();
            }
            return transition.out();
        }).then( () => {
            this.clear();
            this.activeScene = this.transitionScene;
            this.activeScene.alpha = 1;
            // this.addChild(this.transitionScene);
            this.transitionScene = null;
            if (onComplete) {
                onComplete();
            }
        });
    }

    public getScenesTypeCount(sceneName: string): number {
        let count: number = 0;
        this.scenes.findIndex(element => {
            if(element.includes(sceneName)) {
                count++
            }
        });
        return count;
    }

    public setScene(sceneName?: string, options?: ISceneOptions): SceneView {
        if (!sceneName) {
            sceneName = this.defaultSceneName;
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }

        if (!this.hasScene(sceneName)) {
            throw new Error(("No scene found with name " + sceneName + " in layer "));
        }

        if (this.activeSceneName === sceneName && this.activeScene) {
            return this.activeScene;
        } else {
            this.clear();
        }

        // const sceneConfig = this.layersConfig[sceneName];

        this.activeScene = this.createScene(sceneName, options);
        this.activeSceneName = sceneName;
        this.addChild(this.activeScene);

        // if (this.activeScene && this.activeScene instanceof PercentScalableSceneView) {
        //     this.x = 0;
        //     this.y = 0;
        //     this.activeScene.reposition();
        // }
        this.reposition();

        return this.activeScene;
    }

    public hasScene(sceneName: string): boolean {
        return (this.scenes.indexOf(sceneName) !== -1) || (this.scenes.indexOf(sceneName + "~%") !== -1)
    }

    protected isPercentScalable(sceneName: string): boolean {
        return (this.scenes.indexOf(sceneName + "~%") !== -1)
    }

    protected getSceneConfig(sceneName: string): IViewConfigSchema {
        return this.layersConfig[sceneName] || this.layersConfig[sceneName + "~%"];
    }


    protected getPortraitConfig(sceneName: string): IViewConfigSchema {
        if (GameModel.isMobile() && this.secundarylayersConfig ) {
            return this.secundarylayersConfig[sceneName] || this.secundarylayersConfig[sceneName + "~%"];
        }

        return null;
    }

    public getOrphanedScene(sceneName: string): SceneView {
        if (!sceneName) {
            sceneName = this.defaultSceneName;
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }
        let scene = this.createScene(sceneName);
        return scene;
    }

    protected createScene(sceneName: string, options?: ISceneOptions): SceneView {
        const sceneConfig = this.getSceneConfig(sceneName);
        const portraitConfig = this.getPortraitConfig(sceneName);
        const scene = this.isPercentScalable(sceneName) ? new PercentScalableSceneView(sceneConfig,portraitConfig,options) : new SceneView(sceneConfig, portraitConfig,options);
        scene.name = sceneName;
        return scene;

    }

    public clear() {
        // this.removeChildren();
        if (this.activeScene) {
            this.activeScene.removeChildren();
            this.activeScene.destroy();
            this.activeScene = null;
        }
        this.activeSceneName = null;
    }

    public completeClear() {
        this.removeChildren();
        if (this.activeScene) {
            this.activeScene.removeChildren();
            this.activeScene.destroy();
            this.activeScene = null;
        }
        if (this.transitionScene) {
            this.transitionScene.removeChildren();
            this.transitionScene.destroy();
            this.transitionScene = null;
        }
        this.activeSceneName = null;

    }

    public reposition(orientationChanged = false) {
        const rect = Utils.deepClone(this.staticBound) ;
        if (!rect) {
            return;
        }
        const canvasWidth = Layout.canvasWidth;
        const canvasHeight = Layout.canvasHeight;
        rect.x = (canvasWidth / App.instance.stage.scale.x ) / 2 - Layout.scalePixels(rect.width) / 2;
        rect.y = (canvasHeight / App.instance.stage.scale.x ) / 2 - Layout.scalePixels(rect.height) / 2;
        if (this.activeScene && this.activeScene instanceof PercentScalableSceneView) {
            this.x = 0;
            this.y = 0;
            if (!orientationChanged) {
                this.activeScene.reposition();
            }
        } else {
            this.x = rect.x;
            this.y = rect.y;
        }
    }
}
